// The loading-spinner style panel: the storefront-wide spinner ring and its label, in ONE surface
// with a live example pinned above it.
//
// It replaces the Home editor's inline spinner Panel, which was a single color-picker button whose
// popover held all four settings and showed nothing of the result. Same family as the product card,
// category pod and Browse All panels: example on top, flat rows below.
//
// The example is a hand-rolled look-alike of the LIVE spinner, because the two apps disagree on
// Fluent versions: the storefront renders @fluentui/react-components v9's <Spinner> (a rounded arc
// sweeping over a bare track, label below), while this admin app ships Fluent v8, whose spinner
// looks different. The geometry below copies v9's real metrics (diameters 28/32/36/40/44 for
// small..huge, thicker strokes as sizes grow, brand-blue default arc, rounded caps, 1.5s eased
// rotation) so what the merchant sees here is what the shopper gets.
//
// The panel is deliberately dumb: the Home editor passes styleConfig.spinner and takes patches
// back (setSpinner drops empty values, so clearing a row deletes the key).

import React from 'react';
import { Dropdown, Panel, PanelType } from '@fluentui/react';
import { ISpinnerConfig } from './styleConfig';
import { STYLE_PART_ICONS } from './stylesConfig';
import { StyleGroupHeader } from './styleControls';
import { ColorRow, StyleRow, useCellControlStyles } from './styleRows';
import StyleExampleFrame from './StyleExampleFrame';
import FontPicker from '../../Inputs/FontPicker';
import './StorefrontEditor.css';

// Fluent v9 Spinner geometry per size preset (@fluentui/react-spinner useSpinnerStyles): the
// box diameter and the ring's stroke width (strokeWidthThick/Thicker/Thickest = 2/3/4px).
const SPINNER_SIZES: Record<string, { diameter: number; stroke: number }> = {
    'small': { diameter: 28, stroke: 2 },
    'medium': { diameter: 32, stroke: 3 },
    'large': { diameter: 36, stroke: 3 },
    'extra-large': { diameter: 40, stroke: 4 },
    'huge': { diameter: 44, stroke: 4 },
};

// The live default when no color is configured: v9 webLightTheme's brand stroke, the same blue
// the editor already assumes for the banner overlay CTA and carousel dots.
const DEFAULT_SPINNER_COLOR = '#0f6cbd';

// The look-alike ring: a faint full-circle track with a rounded ~70% arc of the configured color
// rotating over it (sf-spinner-example-ring in StorefrontEditor.css carries the 1.5s eased
// rotation, mirroring v9's curveEasyEase cadence).
const SpinnerExample: React.FC<{
    value: ISpinnerConfig;
    labelText: string;
    isDark?: boolean;
}> = ({ value, labelText, isDark }) => {
    const { diameter, stroke } = SPINNER_SIZES[value.size ?? ''] ?? SPINNER_SIZES['huge'];
    const color = value.color || DEFAULT_SPINNER_COLOR;
    const r = (diameter - stroke) / 2;
    const circumference = 2 * Math.PI * r;
    const center = diameter / 2;
    return (
        <StyleExampleFrame isDark={isDark} caption='Example, updates as you edit. Shown while pages load across the whole store.'>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '0.5em 0' }}>
                <svg width={diameter} height={diameter} viewBox={`0 0 ${diameter} ${diameter}`} className='sf-spinner-example-ring' role='presentation'>
                    <circle cx={center} cy={center} r={r} fill='none' stroke={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'} strokeWidth={stroke} />
                    <circle
                        cx={center} cy={center} r={r} fill='none'
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap='round'
                        strokeDasharray={`${circumference * 0.7} ${circumference}`}
                    />
                </svg>
                <span style={{ fontSize: 14, color: value.labelColor || undefined, fontFamily: value.labelFont || undefined }}>
                    {labelText}
                </span>
            </div>
        </StyleExampleFrame>
    );
};

export interface ISpinnerStylePanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    value: ISpinnerConfig;
    onChange: (patch: Partial<ISpinnerConfig>) => void;
    /** The localized loading label, resolved by the editor from the storefront's localization. */
    labelText: string;
    isDark?: boolean;
}

const SpinnerStylePanel: React.FC<ISpinnerStylePanelProps> = (props) => {
    const { value, onChange } = props;
    const cell = useCellControlStyles();
    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Loading Spinner'
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* Pinned above the rows so the effect of every row below is on screen while editing
                (sf-card-example-sticky, StorefrontEditor.css). */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <SpinnerExample value={value} labelText={props.labelText} isDark={props.isDark} />
            </div>

            {/* Two tiny groups, so headers (not accordions): with four rows total there is nothing
                worth folding away. */}
            <StyleGroupHeader
                label='Ring'
                iconName='ProgressRingDots'
                tooltip='The spinning ring itself. The default color is the storefront brand blue.'
            />
            <ColorRow label='Color' value={value.color} onChange={(v) => onChange({ color: v ?? '' })} />
            <StyleRow label='Size'>
                <Dropdown
                    options={[
                        { key: '', text: 'Default (huge)' },
                        { key: 'small', text: 'Small' },
                        { key: 'medium', text: 'Medium' },
                        { key: 'large', text: 'Large' },
                        { key: 'extra-large', text: 'Extra large' },
                        { key: 'huge', text: 'Huge' },
                    ]}
                    selectedKey={value.size ?? ''}
                    onChange={(_, o) => onChange({ size: (o?.key as string) || '' })}
                    styles={cell.dropdown}
                />
            </StyleRow>

            <StyleGroupHeader
                label='Label'
                iconName={STYLE_PART_ICONS.label}
                tooltip='The text under the spinner (e.g. "Loading"). The words themselves come from Localization.'
            />
            <ColorRow label='Color' value={value.labelColor} onChange={(v) => onChange({ labelColor: v ?? '' })} />
            <StyleRow label='Font'>
                <FontPicker
                    selectedFont={value.labelFont ?? ''}
                    placeholder='Default'
                    onChange={(f) => onChange({ labelFont: f || '' })}
                    styles={cell.dropdown}
                />
            </StyleRow>
        </Panel>
    );
};

export default SpinnerStylePanel;
